import * as Sentry from "@sentry/react";

/**
 * One script attributed inside a Long Animation Frame entry. The Long Animation Frame API
 * (Chrome 123+) is not in lib.dom, so its shape is named here.
 */
export type LongAnimationFrameScript = {
  invoker?: string;
  invokerType?: string;
  sourceURL?: string;
  sourceFunctionName?: string;
  sourceCharPosition?: number;
  duration: number;
  executionStart: number;
};

export type LongAnimationFrameEntry = {
  startTime: number;
  duration: number;
  blockingDuration?: number;
  firstUIEventTimestamp?: number;
  renderStart?: number;
  styleAndLayoutStart?: number;
  scripts?: LongAnimationFrameScript[];
};

/** Where the page was when the frame was observed. */
export type FramePage = { origin: string; pathname: string };

/**
 * A frame is reported when it held input up for this long. `blockingDuration` is the browser's measure of that.
 * A frame's `duration` is not: it also spans time in which the page did not paint but nothing ran — render-blocking
 * CSS still downloading, a background tab — and such frames last many seconds with nothing blocked.
 */
export const REPORTED_BLOCKING_MS = 5000;

/** An anonymous page that stays open must not hold reports without bound. */
const MAX_HELD_REPORTS = 10;

const OBJECT_ID = /^[0-9a-f]{24}$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NUMBER = /^\d+$/;
/** Opaque tokens (invitation and reset codes). Route names are words; none is this long with a digit in it. */
const OPAQUE_TOKEN = /^(?=.*\d)[\w-]{16,}$/;

function isRecordId(segment: string): boolean {
  return OBJECT_ID.test(segment) || UUID.test(segment) || NUMBER.test(segment) || OPAQUE_TOKEN.test(segment);
}

/**
 * The route a pathname belongs to, with record ids replaced by `:id` and share-link tokens by `:token`. Sentry
 * groups these events on it, so it must take a bounded set of values: `/designviewer/file/6abcad80…` and
 * `/designviewer/file/6ac86696…` are one screen.
 */
export function routePattern(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const pattern = segments.map((segment, index) => {
    if (segments[index - 1] === "share") return ":token";
    return isRecordId(segment) ? ":id" : segment;
  });
  return `/${pattern.join("/")}`;
}

/** The content hash Vite appends to every chunk name (`main-B5IFW1AT.js`). It changes on every build. */
const CHUNK_HASH = /-[\w-]{8}(?=\.m?js$)/;

/** A build or content hash in a third-party file name (`frame-modern.a1b2c3d4.js`): a run of 6+ with a digit in it. */
const VENDOR_HASH = /[.-](?=[a-z]*\d)[\da-z]{6,}(?=(?:\.min)?\.m?js$)/i;

/**
 * A script URL without the parts that differ per tenant, per build or per object. Our chunks lose the origin (every
 * tenant has its own host) and the content hash; any other same-origin URL is the page itself (an inline
 * `<script>`), so it becomes `inline`. A third-party script keeps its host and file name, without the versioned path
 * between them, a hash in the name, or a record id. A `blob:`, `data:` or extension URL is named by its scheme: the
 * rest is per object or per install.
 */
function scriptName(url: string, page: FramePage): string {
  let parsed: URL;
  try {
    parsed = new URL(url, page.origin);
  } catch {
    // Chrome hands LoAF a URL it loaded, so this does not happen in practice; the label stays bounded if it does,
    // and the event's `extra.scripts` keeps the raw URL.
    return "unparseable-url";
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return parsed.protocol.slice(0, -1);
  if (parsed.origin === page.origin) {
    return /\.m?js$/.test(parsed.pathname) ? parsed.pathname.replace(CHUNK_HASH, "") : "inline";
  }
  const file = parsed.pathname.split("/").pop() ?? "";
  // The label keeps the URL's shape (`host/file`), so it is built through URL, which encodes the file name.
  parsed.pathname = isRecordId(file) ? ":id" : file.replace(VENDOR_HASH, "");
  return `${parsed.host}${parsed.pathname}`;
}

/**
 * Chrome's name for the code that ran: `DIV#pdfContainer.onwheel`, `XMLHttpRequest.onloadend`,
 * `TimerHandler:setTimeout`, or a script URL. Element ids are dropped (React's `_r_…` ids differ per render, and an
 * id may itself contain dots: everything up to the final `.on<event>` goes), and so are `[src=…]` qualifiers.
 */
function invokerName(script: LongAnimationFrameScript, page: FramePage): string {
  if (script.invokerType === "module-script" || script.invokerType === "classic-script") {
    return scriptName(script.invoker || script.sourceURL || "", page);
  }
  const invoker = (script.invoker ?? "")
    .replace(/\[src=[^\]]*\]/g, "")
    .replace(/^([A-Za-z][\w-]*)#.*(?=\.[^.]*$)/, "$1");
  return invoker || "unknown";
}

function slowestScript<T extends { duration: number }>(scripts: T[]): T | undefined {
  return scripts.reduce<T | undefined>(
    (worst, script) => (worst && worst.duration >= script.duration ? worst : script),
    undefined,
  );
}

/**
 * The part of the frame that blocked: `<invokerType>:<invoker>` of the slowest script, or `rendering` when scripts
 * ran for less than half of the blocked time (style, layout and paint, which LoAF attributes to no script).
 *
 * The invoker is where the browser entered our code, not where the time went. React's render work enters through
 * `MessagePort.onmessage` (its scheduler), and every listener Sentry wraps enters through Sentry's wrapper, whose
 * source position is the same for all of them. The event's `extra.scripts` keeps every script with its position.
 */
export function blockerLabel(entry: LongAnimationFrameEntry, page: FramePage): string {
  const scripts = entry.scripts ?? [];
  const slowest = slowestScript(scripts);
  const scriptTime = scripts.reduce((sum, script) => sum + script.duration, 0);
  if (!slowest || scriptTime < (entry.blockingDuration ?? 0) / 2) return "rendering";
  return `${slowest.invokerType ?? "unknown"}:${invokerName(slowest, page)}`;
}

export type LongFrameReport = {
  message: string;
  level: "warning";
  timestamp: number;
  fingerprint: string[];
  tags: Record<string, string | number>;
  extra: Record<string, unknown>;
};

/**
 * The Sentry event for a frame, or `null` when the frame did not block input for {@link REPORTED_BLOCKING_MS}.
 * `timeOrigin` turns the entry's page-relative start into wall-clock time, so a report held until sign-in still
 * carries the moment the frame happened.
 */
export function describeLongFrame(
  entry: LongAnimationFrameEntry,
  page: FramePage,
  timeOrigin: number,
): LongFrameReport | null {
  const blocked = entry.blockingDuration ?? 0;
  if (blocked < REPORTED_BLOCKING_MS) return null;

  const scripts = (entry.scripts ?? []).map((script) => ({
    invoker: script.invoker,
    invokerType: script.invokerType,
    sourceURL: script.sourceURL,
    sourceFunctionName: script.sourceFunctionName,
    sourceCharPosition: script.sourceCharPosition,
    duration: Math.round(script.duration),
    executionStart: Math.round(script.executionStart),
  }));
  const slowest = slowestScript(scripts);
  const blocker = blockerLabel(entry, page);
  const route = routePattern(page.pathname);

  return {
    message: `Main thread blocked for ${Math.round(blocked)}ms: ${blocker} on ${route}`,
    level: "warning",
    timestamp: (timeOrigin + entry.startTime) / 1000,
    fingerprint: ["main-thread-blocked", blocker, route],
    tags: {
      "thread.blocked_duration": Math.round(blocked),
      "thread.frame_duration": Math.round(entry.duration),
      "thread.blocker": blocker,
      "thread.route": route,
      "thread.blocker_type": slowest?.invokerType ?? "N/A",
      "thread.blocker_invoker": slowest ? invokerName(slowest, page) : "N/A",
      "thread.blocker_file": slowest?.sourceURL ? scriptName(slowest.sourceURL, page) : "N/A",
      "thread.blocker_function": slowest?.sourceFunctionName || "N/A",
      "thread.blocker_char_pos": slowest?.sourceCharPosition ?? "N/A",
      "thread.scripts_count": scripts.length,
    },
    extra: {
      duration: entry.duration,
      blockingDuration: entry.blockingDuration,
      firstUIEventTimestamp: entry.firstUIEventTimestamp,
      renderStart: entry.renderStart,
      styleAndLayoutStart: entry.styleAndLayoutStart,
      scripts,
    },
  };
}

/** The Sentry scope whose user a report waits for: `Sentry.setUser` writes it, and it notifies its listeners. */
export type UserScope = Pick<Sentry.Scope, "getUser" | "addScopeListener">;

/**
 * Sends frame reports while the Sentry scope carries a signed-in user, and holds them until it does.
 *
 * Email security scanners (Microsoft 365 link scanning: Azure IPs, a Los Angeles timezone, months-old Chrome) open
 * every link in our notification mails inside slow VMs, so their app start-up blocks for seconds; none of their
 * events ever carries a user. Holding reports until the scope has a user excludes them, still sends the start-up
 * frames of real users (identified a few seconds after the app boots), and drops the frames of visitors who never
 * sign in.
 *
 * The reporter watches the scope itself rather than waiting to be told, so no caller can forget to release it: the
 * moment the app's `Sentry.setUser` lands, the held reports go out with that user on them.
 */
export function createLongFrameReporter(send: (report: LongFrameReport) => void, scope: UserScope) {
  const held: LongFrameReport[] = [];
  const identified = () => scope.getUser()?.id !== undefined;
  scope.addScopeListener(() => {
    if (identified()) for (const report of held.splice(0)) send(report);
  });
  return {
    report(report: LongFrameReport): void {
      if (identified()) {
        send(report);
      } else if (held.length < MAX_HELD_REPORTS) {
        held.push(report);
      }
    },
  };
}

/**
 * Observes long animation frames for the life of the page. A browser with Long Tasks but not the Long Animation
 * Frame API (Chrome before 123) gets breadcrumbs instead, so its error reports still show what blocked.
 */
export function startLongFrameMonitor(): void {
  if (!("PerformanceObserver" in window)) return;
  const supported = PerformanceObserver.supportedEntryTypes;

  if (supported.includes("long-animation-frame")) {
    // `Sentry.setUser` writes the isolation scope (a test pins this), so that is the scope whose user is awaited.
    const reporter = createLongFrameReporter((report) => {
      Sentry.captureEvent(report);
    }, Sentry.getIsolationScope());
    new PerformanceObserver((list) => {
      const page = { origin: window.location.origin, pathname: window.location.pathname };
      for (const entry of list.getEntries()) {
        const frame = entry as PerformanceEntry & LongAnimationFrameEntry;
        const report = describeLongFrame(frame, page, performance.timeOrigin);
        if (report) reporter.report(report);
      }
    }).observe({ type: "long-animation-frame", buffered: true });
  } else if (supported.includes("longtask")) {
    new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration < 500) continue;
        Sentry.addBreadcrumb({
          category: "long-task",
          message: `Main thread blocked for ${Math.round(entry.duration)}ms`,
          level: "warning",
          data: { duration: entry.duration, name: entry.name },
        });
      }
    }).observe({ type: "longtask", buffered: true });
  }
}
